import { MAX_ATTACHMENT_SIZE_MB } from './attachments';

export const LOTTERY_COPY = {
  featureName: 'Lottery',
  list: {
    title: 'Lottery',
    searchPlaceholder: 'Search batches...',
    searchAria: 'Search batches',
    pullButton: 'Pull a Batch',
    healthButton: 'External Services Health',
    syncButton: 'Sync existing batches',
    syncing: 'Syncing\u2026',
    neverSynced: 'Never synced',
    syncInProgress: 'A batch sync is already in progress.',
    syncHelpAria: 'About syncing existing batches',
    syncHelpBody:
      'Sync checks Housing Connect for batches that already exist but aren\u2019t shown here yet, and adds them. Use it when a batch was created in Housing Connect outside of Pronto, so you can see and work with its applicants. It only adds missing batches. Existing ones are never changed.',
    emptyTitle: 'No batches yet',
    emptyBody: 'Pull a batch to bring applicants in from the lottery.',
    error: 'We couldn\u2019t load the batch list.',
    noLotteryTitle: 'Lottery isn\u2019t set up for this property',
    noLotteryBody: 'A Lottery must be created before you can pull batches.',
    retry: 'Retry',
  },
  wizard: {
    title: 'Pull a batch',
    subheader:
      'Configure the pull and communication settings. Everything the system needs is collected here up front.',
    steps: [
      'Pull Batch Settings',
      'Communications Settings',
      'Certification & Status Notes Settings',
    ],
    nextButton: 'Next',
    nextStep: 'Next Step',
    previousStep: 'Previous Step',
    startPull: 'Start Pull Batch',
    stepTwoHelper:
      'Select any optional settings to be applied to this communication.',
    amiHelper:
      'Each selection allows applicants whose AMI and unit size match that pair.',
    sendStatusesHelper:
      'Applicants with any selected status will also be loaded, in addition to those matched by the AMI and unit size filters.',
    deadlineHelper:
      'Housing Connect starts its own 14-day document clock when Request For Documents is written.',
    viewExistingBatch: 'View that batch',
    subjectPlaceholder: 'Subject',
    clearSubject: 'Clear email subject',
    completedStep: 'Completed',
    submitNetworkError:
      'We couldn\u2019t confirm whether the batch was requested. Check the batch list before trying again — the request is never retried automatically.',
    noInviteNotice:
      'No invitation will be sent. Email and SMS are both off, so certifications will be created and set up but applicants will not be contacted.',
    validation: {
      batchSize: 'Enter a batch size between 1 and 100.',
      preferenceGroup: 'Select a preference group.',
      deadline: 'Select a deadline date that is not in the past.',
      emailBody: 'Add an email body.',
      smsBody: 'Add an SMS body.',
    },
    fields: {
      batchSize: 'Batch size',
      preferenceGroup: 'Preference group',
      sendStatuses: 'Loadable Applicant Statuses',
      amiUnitSize: 'AMI & unit size (optional)',
      deadline: 'Set a Deadline Date',
      communicationType: 'What type of communication do you want to send?',
      emailChannel: 'Email',
      smsChannel: 'SMS',
      emailSection: 'Email',
      smsSection: 'SMS',
      attachments: {
        hint: `PDF, Word, or an image. Up to ${MAX_ATTACHMENT_SIZE_MB} MB total.`,
      },
      emailSubject: 'Email subject',
      emailBody: 'Email body',
      smsBody: 'SMS body',
      templates: 'Templates',
      statusChange: 'Add a Status Change',
      complianceUser: 'Assign a Compliance User As File Owner',
      statusNoteCategory: 'Set a Status Note Category',
      statusNoteText: 'Set a Status Note Text',
      selectTemplate: 'Use a template',
      manageTemplatesTitle: 'Conversation Templates',
    },
    amiPairGroups: {
      propertyUsed: 'Property used Unit Sizes@AMI',
      other: 'Other pairs',
    },
  },
  run: {
    breadcrumb: 'Lottery',
    summaryTitle: 'Run summary',
    attachments: {
      title: 'Attachments',
    },
    freshEmptyTitle: 'Pulling batch from Housing Connect',
    freshEmptyBody: 'Applicants will appear here once the batch is ready.',
    livePaused: 'Live updates paused',
    details: {
      pullSection: 'Pull',
      certificationConfig: 'Configuration',
      applicantPool: 'Applicant pool',
      preferenceGroup: 'Preference group',
      amis: 'AMIs',
      batchSize: 'Batch size',
      communicationsSection: 'Communications',
      channels: 'Channels',
      emailTemplate: 'Email template',
      smsTemplate: 'SMS template',
      certificationSection: 'Certification & status notes',
      complianceUser: 'Assign a Compliance User',
      deadlineDate: 'Deadline date',
      statusChange: 'Status change',
      statusNoteCategory: 'Status note category',
      statusNoteText: 'Status note text',
    },
    steps: {
      running: 'running',
      pending: 'Pending',
      completed: 'Completed',
      failed: 'Failed',
      skipped: 'Skipped',
    },
    stats: {
      applicants: 'Applicants',
      duplicates: 'Duplicates',
      rejected: 'Rejected',
      ineligible: 'Ineligible',
      setAsPending: 'Set as pending',
      creating: 'Creating',
      certsCreated: 'Files created',
      certsSubmitted: 'Certs submitted',
      invitesSent: 'Invites sent',
      failed: 'Failed',
      donutAria: 'Applicant breakdown',
    },
  },
  table: {
    searchPlaceholder: 'Search log # or name...',
    noName: '(no name)',
    clearFilters: 'Clear filters',
    filteredEmpty: 'No applicants match your filters.',
    error: 'We couldn\u2019t load the applicants.',
    loadApplicantsHelper:
      'Only Set as Pending or applicants without a certification can be loaded.',
    emptyTitle: 'No applicants',
    emptyBody: 'No applicants were pulled for this batch.',
    emailSent: 'Email sent',
    emailNotSent: 'Email not sent',
    manuallyLoaded: 'Manually loaded',
    loadOutcomeFailedTooltip:
      'Certification was created, but the invite failed.',
    emailNotSentGeneric:
      'No verified email address on file, the household was not invited by email.',
    searchAria: 'Search applicants',
  },
  manualLoad: {
    title: 'Load applicants into Pronto',
    defaultNoteText:
      'Manually loaded from Housing Connect \u2014 household matched an available unit.',
    fileOwner: 'File owner',
    deadlineLabel: 'Deadline date',
    loadingApplicants: 'Loading applicants\u2026',
    cancel: 'Cancel',
    noLongerEligible:
      'Some applicants are no longer eligible to load. Refresh to see their current state.',
    refresh: 'Refresh',
    communicationsHeading: 'Communications',
    communicationsNote:
      'Seeded from this batch\u2019s settings. Edit the message sent with this load.',
  },
  tray: {
    header: 'Pulling Lottery Batch(es)',
    viewAll: 'View all batches',
    ariaLabel: 'Lottery batch progress',
    updatesPaused: 'Live updates paused',
  },
  statusReference: {
    title: 'Lottery batch run — status reference',
    subtitle:
      'Every reachable combination of Housing Connect status and Pronto load outcome. Chip colours match the batch screens.',
    neverOccurTitle: 'Combinations that never occur',
    dispositionColumn: 'Disposition',
    loadOutcomeColumn: 'Load outcome',
    meaningColumn: 'What it means',
    close: 'Close',
  },
  degraded: {
    title: 'External service is degraded',
    body: 'The external service used to pull and process lottery batches is responding slowly. New batch runs are paused until it recovers — runs already in flight are unaffected.',
    pullDisabled:
      'New batch runs are paused while the external service is degraded.',
  },
  unavailable: {
    title: 'External service is unavailable',
    body: 'The external service used to pull and process lottery batches is unavailable. New batch runs are paused until it recovers — runs already in flight are unaffected.',
  },
  health: {
    title: 'External Services Health',
    subtitle: 'Live status of the services the Lottery feature depends on.',
    serviceName: 'Housing Connect 2 (HC2)',
    providerLabels: {
      hc2: 'Housing Connect 2',
    } as Record<string, string>,
    statusLabels: {
      operational: 'Operational',
      degraded: 'Degraded',
      unavailable: 'Unavailable',
      unknown: 'Unknown',
    },
    tooltips: {
      operational: 'Housing Connect 2 (HC2) is operational.',
      degraded: 'Housing Connect 2 (HC2) is responding slowly.',
      unavailable: 'Housing Connect 2 (HC2) is unavailable.',
      unknown: 'External services health hasn\u2019t been checked yet.',
    },
    notCheckedYet: 'Not checked yet',
    noDataTitle: 'No health data yet',
    noDataBody:
      'Live status of the external services isn\u2019t available yet. Try again in a moment.',
    retry: 'Retry',
    close: 'Close',
  },
  errors: {
    lotteries: 'We couldn\u2019t load the lottery.',
    wizardOptions: 'We couldn\u2019t load the pull settings.',
    batches: 'We couldn\u2019t load the batch list.',
    batch: 'We couldn\u2019t load this batch.',
    applicants: 'We couldn\u2019t load the applicants.',
    facets: 'We couldn\u2019t load the filter counts.',
    activeBatches: 'We couldn\u2019t load your active batches.',
    statusReference: 'We couldn\u2019t load the status reference.',
    createBatch: 'We couldn\u2019t start the batch.',
    manualLoad: 'We couldn\u2019t load the applicants.',
    syncBatches: 'We couldn\u2019t sync batches.',
  },
};

export function formatRunSubtitle(pulledBy: string, createdAt: string): string {
  return `Pulled by ${pulledBy} · ${createdAt}`;
}

export function formatLastSynced(timeAgo: string): string {
  return `Last synced ${timeAgo}`;
}

export function formatChannels(sendEmail: boolean, sendSms: boolean): string {
  if (sendEmail && sendSms) {
    return 'Email and SMS';
  }
  if (sendEmail) {
    return 'Email';
  }
  if (sendSms) {
    return 'SMS';
  }
  return '—';
}

export function formatWizardConfirmation(
  propertyName: string,
  certificationConfig: string,
  applicantPool: string,
  channels: string
): string {
  return `You're about to send a message to ${propertyName} property, ${certificationConfig} certification configuration, and to ${applicantPool} applicant pool; via ${channels} to all created certifications from this batch. Please write your message or select a template from the Templates button.`;
}

export function formatWizardNoInviteConfirmation(
  propertyName: string,
  certificationConfig: string,
  applicantPool: string
): string {
  return `You're about to create certifications for ${propertyName} property, ${certificationConfig} certification configuration, and ${applicantPool} applicant pool with no invitation — Email and SMS are both off. Certifications will still be created and set up.`;
}

export function formatExistingBatchConflict(preferenceGroup: string): string {
  return `A batch for ${preferenceGroup} is already in progress.`;
}

export function formatPartialCompletion(count: number): string {
  const noun = count === 1 ? 'applicant' : 'applicants';
  return `The batch finished. ${count} ${noun} could not be loaded.`;
}

export function formatBackfilledAlert(timeAgo: string): string {
  return `This batch was pulled ${timeAgo}. External service status could vary from what you see here.`;
}

export function formatApplicantsTitle(count: number): string {
  return `Applicants (${count})`;
}

export function formatSelectedApplicants(count: number): string {
  return `Selected Applicants (${count})`;
}

export function formatLoadApplicantsButton(count: number): string {
  return count > 0 ? `Load applicants (${count})` : 'Load applicants';
}

export function formatLoadApplicantsSubmit(count: number): string {
  return count === 1 ? 'Load 1 applicant' : `Load ${count} applicants`;
}

export function formatManualLoadSummary(count: number): string {
  const noun = count === 1 ? 'applicant' : 'applicants';
  return `${count} ${noun} will be created and invited. Request For Documents is written to Housing Connect after the invite.`;
}

export function formatManualLoadInFlight(count: number): string {
  const noun = count === 1 ? 'applicant' : 'applicants';
  return `Loading ${count} ${noun} into Pronto… This can take a few minutes.`;
}

export function formatInvitedAt(formattedTimestamp: string): string {
  return `Invited ${formattedTimestamp}`;
}

export function formatAttachmentTypeRejected(name: string): string {
  return `"${name}" isn\u2019t a PDF, Word document, or image.`;
}

export function formatAttachmentTooLarge(name: string): string {
  return `"${name}" is larger than ${MAX_ATTACHMENT_SIZE_MB} MB.`;
}

export function formatAttachmentDuplicate(name: string): string {
  return `"${name}" is already attached.`;
}

export function formatAttachmentTotalExceeded(name: string): string {
  return `Adding "${name}" would exceed the ${MAX_ATTACHMENT_SIZE_MB} MB attachment limit.`;
}
